import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { debounce } from "lodash"
import { useShallow } from "zustand/react/shallow"
import { Affix } from "antd"
import scrollToElement from "utilities/scrollToElement"
import { Select } from "components-design-system"
import useAuthoringActions from "../../hooks/actions/useAuthoringActions"

// components
import { Collapse } from "antd"
import CollapsePanelButton from "components/CollapsePanelButton/CollapsePanelButton"
import { formNavItems, SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring"
import "./FormNavigator.scss"

// hooks
import { getSchemaRegistryTitle } from "utilities/authoringFunctions.js"
import useAuthoringViewStore from "../../hooks/stores/useAuthoringViewStore"
import useAuthoringValidationActions from "../../hooks/actions/useAuthoringValidationActions"
import useAuthoringDataStore from "../../hooks/stores/useAuthoringDataStore"

const FormNavigator = ({ sectDropdownOptions, onChangeSection, className }) => {
  const { formData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
    }))
  )

  const { formView, setFormView, formSection, isReviewerView, showNav, setShowNav, formStatus } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      setFormView: state.setFormView,
      formSection: state.formSection,
      isReviewerView: state.isReviewerView,
      showNav: state.showNav,
      setShowNav: state.setShowNav,
      formStatus: state.formStatus,
    }))
  )
  const [showCollapse, setShowCollapse] = useState(false)
  const [nav, setNav] = useState([])
  const [activeNavItem, setActiveNavItem] = useState(null)
  const formNavRef = useRef()
  const canHighlightSectionRef = useRef(true)
  const activeNavItemRef = useRef(activeNavItem)
  const [activeKey, setActiveKey] = useState(sectDropdownOptions?.[0])
  const { registryOptions } = useAuthoringActions()

  const { clearValidations } = useAuthoringValidationActions()

  const container = isReviewerView
    ? document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.REVIEW_ID)[0]
    : document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]

  const studyType = formData?.protocol?.study_type

  useEffect(() => {
    if (sectDropdownOptions?.[0]) setActiveKey(sectDropdownOptions[0])
  }, [sectDropdownOptions?.[0]])

  useEffect(() => {
    if (container) {
      container?.addEventListener("scroll", isInViewportDebounce)

      return () => {
        container?.removeEventListener("scroll", isInViewportDebounce)
      }
    }
  }, [nav, container])

  const isInViewport = () => {
    if (canHighlightSectionRef.current !== false) {
      let mostVisible = {
        nav: nav[0],
      }

      let visibleNav = null

      for (let i = 0; i < nav.length; i++) {
        let curr = nav[i]
        let el = document.getElementById(curr.jpath)

        let visible = checkInView(container, el, true)
        if (visible) {
          visibleNav = curr
          break
        }
      }

      mostVisible = {
        nav: visibleNav,
      }

      if (mostVisible.nav) {
        setActiveNavItem(mostVisible.nav)
        activeNavItemRef.current = mostVisible.nav
      }
    }
  }

  let isInViewportDebounce = debounce(isInViewport, 100)

  useEffect(() => {
    let headerEls = document.getElementsByClassName("form-navigator-header")

    if (headerEls.length > 0) {
      for (let i = 0; i < headerEls.length; i++) {
        let headerEl = headerEls[i]
        let parent = headerEl.querySelector(".ant-collapse-header")
        let arrowEl = headerEl.querySelector(".ant-collapse-expand-icon")
        let labelEl = headerEl.querySelector(".ant-collapse-header-text")
        if (parent && arrowEl) {
          parent.removeChild(arrowEl)
          arrowEl.style.marginLeft = "6px"

          if (labelEl.innerHTML !== "Delayed Results Certification" && labelEl.innerHTML !== "Sponsor Trial Website") {
            parent.appendChild(arrowEl)
          }
        }
      }
    }
  }, [nav])

  const filterSectionItemsOnConditions = (arr) => {
    return arr.filter((item) => {
      if (
        studyType === "Expanded Access" &&
        formView?.id === "ctg" &&
        formSection?.id === "protocol" &&
        item?.jpath === "root_protocol_study_outcome_measures-container"
      ) {
        return false
      }
      return true
    })
  }

  useEffect(() => {
    if (formSection && formView) {
      const nav = []
      const orderSet = new Set()
      const orderArr = formNavItems?.[formSection?.id]?.order?.[formView?.id]

      const onAddMenuItem = (jpath, navItem) => {
        nav.push({
          jpath,
          title: navItem.registries[formView.id],
          style: navItem.style,
        })
      }

      if (orderArr) {
        orderArr.forEach((jpath) => {
          const navItem = formNavItems?.[formSection.id]?.properties[jpath]
          onAddMenuItem(jpath, navItem)
          orderSet.add(jpath)
        })
      }

      if (formNavItems?.[formSection.id]?.properties) {
        Object.keys(formNavItems?.[formSection.id]?.properties)?.forEach((jpath) => {
          const navItem = formNavItems?.[formSection.id]?.properties[jpath]

          if (!orderSet.has(jpath) && navItem.registries?.[formView?.id]) {
            onAddMenuItem(jpath, navItem)
          }
        })
      }

      setNav(nav)
      setActiveNavItem(nav?.[0])
      activeNavItemRef.current = nav?.[0]
    }
  }, [formView, formSection, formStatus])

  const scrollToField = async (item) => {
    canHighlightSectionRef.current = false

    let jpath = item.jpath
    if (item.jpath === "root_protocol_study_identification-container") jpath = "root_protocol"
    const element = document.getElementById(jpath)

    if (element && container) {
      scrollToElement({
        scrollableElement: container,
        targetElement: element,
        scrollType: "instant",
        offsetTop: isReviewerView ? 0 : 70,
      })

      setActiveNavItem(item)
      activeNavItemRef.current = item

      await new Promise((r) => setTimeout(r, 2000))
      canHighlightSectionRef.current = true
    }
  }

  Math.easeInOutQuad = function (t, b, c, d) {
    t /= d / 2
    if (t < 1) return (c / 2) * t * t + b
    t--
    return (-c / 2) * (t * (t - 2) - 1) + b
  }

  const checkInView = (container, element, partial) => {
    const containerOffset = isReviewerView ? 300 : 100
    if (element) {
      //Get container properties
      let cTop = container.scrollTop
      let cBottom = cTop + container.clientHeight

      //Get element properties
      let eTop = element.offsetTop - containerOffset
      let eBottom = eTop + element.clientHeight

      //Check if in view
      let isTotal = eTop >= cTop && eBottom <= cBottom

      let isPartial = partial && ((eTop < cTop && eBottom > cTop) || (eBottom > cBottom && eTop < cBottom))

      //Return outcome
      return isTotal || isPartial
    }
  }

  const getSectClassname = (sect) => {
    return sect.toLowerCase().replace(/\s/g, "-")
  }

  const onToggleNav = () => {
    let show = !showNav
    setShowNav(show)
    localStorage.setItem("SHOW_AUTHORING_FORM_NAV", show)

    const parentElement = formNavRef.current.parentElement.parentElement

    if (parentElement) {
      // Get all child elements of the parent element
      const childElements = parentElement.querySelectorAll("*")

      // Loop through each child element
      for (let i = 0; i < childElements.length; i++) {
        const childElement = childElements[i]
        // Check if the child element has the aria-hidden attribute set to true
        if (childElement.getAttribute("aria-hidden") === "true" && childElement.className === "") {
          // This is the child element you are looking for
          childElement.style.width = show ? "225px" : "0px"
          break
        }
      }
    }
  }

  const sectionItems = React.useMemo(() => {
    return sectDropdownOptions.map((section) => {
      return {
        key: section,
        label: (
          <div
            className={`form-navigator-panel ${getSectClassname(section)}`}
            key={section}
            onClick={() => {
              setActiveKey(section)
              if (activeKey !== section) onChangeSection(section)
            }}
          >
            {section}
          </div>
        ),
        children: (
          <div className="flex flex-col">
            {filterSectionItemsOnConditions(nav).map((item) => {
              return (
                <div
                  className={`nav-item ${activeNavItem?.jpath === item.jpath ? "nav-item-active" : ""}`}
                  key={`${section}-${item.title}`}
                  onClick={async () => {
                    scrollToField(item)
                  }}
                  style={{
                    marginLeft: item?.style?.indent ? 8 : 0,
                  }}
                >
                  {item.title}
                </div>
              )
            })}
          </div>
        ),
      }
    })
  }, [sectDropdownOptions, nav, formSection, formView, activeNavItem, studyType])

  const onRegChange = (regId) => {
    if (
      formSection &&
      formView &&
      regId
      // only if more than one button
      // registries?.length > 1
    ) {
      const text = getSchemaRegistryTitle(formSection?.id, regId)
      let formViewObject = { id: regId, text }
      setFormView(formViewObject)
      clearValidations()
    }
  }

  if (!sectDropdownOptions || sectDropdownOptions.length === 0) return null

  let collapsePanelButtonClassNames = ["auth-form-collapse-navigator-container"]
  let collapsePanelClassNames = ["form-nav-collapse"]
  if (showNav) collapsePanelButtonClassNames.push("auth-form-collapse-navigator-closed")
  if (showCollapse === false && showNav === true) collapsePanelButtonClassNames.push("hidden")
  if (showNav === false) {
    collapsePanelClassNames.push("form-nav-collapse-closed")
  }

  return (
    <Affix
      target={() => document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]}
      offsetTop={43}
      style={{ zIndex: 3 }}
    >
      <div
        ref={formNavRef}
        id="form-navigator-container"
        className={`form-navigator${className ? ` ${className}` : ""}${isReviewerView ? " reviewer-view" : ""}${
          showNav ? " --shown" : " --hidden"
        }`}
        onMouseEnter={() => setShowCollapse(true)}
        onMouseLeave={() => setShowCollapse(false)}
      >
        {isReviewerView !== true && showNav && (
          <div className="registry-selector-container">
            <Select
              value={{ value: formView?.id, label: formView?.text }}
              options={registryOptions}
              label="Registry"
              onChange={onRegChange}
              style={{ minWidth: 150 }}
              allowClear={false}
            />
          </div>
        )}
        <CollapsePanelButton
          open={showNav}
          setOpen={onToggleNav}
          placement={"left"}
          className={collapsePanelButtonClassNames.join(" ")}
        />
        <Collapse
          accordion
          bordered={false}
          onChange={(val) => {
            setActiveKey(val[0])
            if (onChangeSection) onChangeSection(val[0])
          }}
          expandIcon={({ isActive }) =>
            isActive ? <i className="fa-solid fa-caret-up" /> : <i className="fa-solid fa-caret-down" />
          }
          className={collapsePanelClassNames.join(" ")}
          items={sectionItems}
        />
      </div>
    </Affix>
  )
}

FormNavigator.propTypes = {
  onChangeSection: PropTypes.func,
  className: PropTypes.string,
}

export default FormNavigator
